import React from "react";
import {
  Box,
  Typography,
  Avatar,
  Divider,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  AccessTime,
  Timer,
  Person,
  CheckCircle,
  Pending,
  Schedule,
  LocationOn,
  Warning,
  Error as ErrorIcon,
  HelpOutline,
} from "@mui/icons-material";
import {
  formatTimeRange,
  getDurationFromDateTime,
} from "../../../base/FormatTime";

const PUNCTUALITY_ICONS = {
  check: CheckCircle,
  clock: AccessTime,
  warning: Warning,
  alert: ErrorIcon,
  help: HelpOutline,
};

const PunctualityBadge = ({ punctuality }) => {
  const theme = useTheme();

  if (!punctuality || punctuality.status === "UNKNOWN") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          p: 0.85,
          flex: 1,
          bgcolor: "#F8FAFC",
          borderRadius: "6px",
        }}
      >
        <HelpOutline sx={{ fontSize: 14, color: "#94A3B8" }} />
        <Typography
          variant="body2"
          fontWeight={600}
          color="#94A3B8"
          fontSize="0.7rem"
        >
          No Data
        </Typography>
      </Box>
    );
  }

  const IconComponent = PUNCTUALITY_ICONS[punctuality.icon] || CheckCircle;

  const displayText =
    punctuality.direction === "on_time"
      ? "On Time"
      : `${punctuality.minutes}m ${punctuality.direction}`;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        p: 0.85,
        flex: 1,
        bgcolor: punctuality.bg_color,
        borderRadius: "6px",
        border: `1px solid ${punctuality.border_color}`,
      }}
    >
      <IconComponent sx={{ fontSize: 14, color: punctuality.color }} />
      <Typography
        variant="body2"
        fontWeight={700}
        color={punctuality.color}
        fontSize="0.7rem"
      >
        {displayText}
      </Typography>
    </Box>
  );
};

const ExecutionDetailsCard = ({
  executionDetails,
  scheduledStart,
  scheduledEnd,
  rescheduledStart,
  rescheduledEnd,
}) => {
  const theme = useTheme();

  if (!executionDetails || executionDetails.length === 0) {
    return null;
  }

  const getStatus = (hasStarted, hasEnded) => {
    if (!hasStarted)
      return { label: "Pending", color: "#94A3B8", bg: "#F1F5F9" };
    if (!hasEnded)
      return { label: "Active", color: "#F59E0B", bg: "#FEF3C7" };
    return { label: "Completed", color: "#22C55E", bg: "#D1FAE5" };
  };

  const hasRescheduled = rescheduledStart && rescheduledEnd;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "6px",
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Person sx={{ fontSize: 16, color: theme.palette.primary.main }} />
        </Box>
        <Typography
          variant="body2"
          fontWeight={600}
          color="#1A1A2E"
          fontSize="0.85rem"
        >
          Staff Execution
        </Typography>
        <Chip
          label={executionDetails.length}
          size="small"
          sx={{
            height: 18,
            fontSize: "0.62rem",
            fontWeight: 600,
            bgcolor: "#F1F5F9",
            color: "#475467",
          }}
        />
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        {executionDetails.map((staff) => {
          const hasStarted = !!staff.actual_start;
          const hasEnded = !!staff.actual_end;
          const status = getStatus(hasStarted, hasEnded);
          const punctuality = staff.punctuality;
          const isLate = punctuality?.is_late === true;

          return (
            <Box
              key={staff.id}
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(33.33% - 12px)",
                },
                minWidth: { xs: "100%", sm: "260px" },
                borderRadius: "10px",
                border: `1px solid ${
                  isLate ? "#FDA29B" : "#E2E8F0"
                }`,
                bgcolor: isLate ? "#FFFBF9" : "#FFFFFF",
                p: 1.5,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: isLate
                    ? "#F04438"
                    : hasStarted
                    ? hasEnded
                      ? theme.palette.success.main
                      : theme.palette.warning.main
                    : "#94A3B8",
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 1.25,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: "11px",
                      fontWeight: 700,
                      bgcolor: alpha(
                        hasStarted
                          ? hasEnded
                            ? theme.palette.success.main
                            : theme.palette.warning.main
                          : "#94A3B8",
                        0.1
                      ),
                      color: hasStarted
                        ? hasEnded
                          ? theme.palette.success.main
                          : theme.palette.warning.main
                        : "#94A3B8",
                    }}
                  >
                    {staff.first_name?.charAt(0)}
                    {staff.last_name?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#1A1A2E"
                      fontSize="0.82rem"
                    >
                      {staff.first_name} {staff.last_name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="#6B7280"
                      fontSize="0.7rem"
                    >
                      {staff.role || "Carer"}
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={status.label}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.58rem",
                    fontWeight: 600,
                    bgcolor: status.bg,
                    color: status.color,
                  }}
                />
              </Box>

              <Divider sx={{ mb: 1.25 }} />

              <Box
                sx={{
                  p: 0.85,
                  bgcolor: hasRescheduled ? "#FEF3C7" : "#F8FAFC",
                  borderRadius: "6px",
                  mb: 1.25,
                  border: hasRescheduled ? "1px solid #F59E0B" : "none",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Schedule
                    sx={{
                      fontSize: 14,
                      color: hasRescheduled ? "#F59E0B" : "#64748B",
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="#6B7280"
                    fontWeight={500}
                    sx={{ minWidth: 68, fontSize: "0.68rem" }}
                  >
                    {hasRescheduled ? "Rescheduled" : "Scheduled"}
                  </Typography>
                  {hasRescheduled ? (
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="#D97706"
                      fontSize="0.75rem"
                    >
                      {formatTimeRange(rescheduledStart, rescheduledEnd)}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#1A1A2E"
                      fontSize="0.75rem"
                    >
                      {formatTimeRange(scheduledStart, scheduledEnd)}
                    </Typography>
                  )}
                </Box>
                {hasRescheduled && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      pl: 3.5,
                      mt: 0.25,
                    }}
                  >
                    <AccessTime sx={{ fontSize: 12, color: "#94A3B8" }} />
                    <Typography
                      variant="caption"
                      color="#94A3B8"
                      sx={{
                        textDecoration: "line-through",
                        fontSize: "0.6rem",
                      }}
                    >
                      {formatTimeRange(scheduledStart, scheduledEnd)}
                    </Typography>
                  </Box>
                )}
              </Box>

              {hasStarted ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      p: 0.85,
                      bgcolor: alpha(theme.palette.success.main, 0.04),
                      borderRadius: "6px",
                    }}
                  >
                    <AccessTime
                      sx={{
                        fontSize: 14,
                        color: theme.palette.success.main,
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="#6B7280"
                      fontWeight={500}
                      sx={{ minWidth: 50, fontSize: "0.68rem" }}
                    >
                      {hasEnded ? "Worked" : "Started"}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#1A1A2E"
                      fontSize="0.75rem"
                    >
                      {formatTimeRange(
                        staff.actual_start,
                        staff.actual_end || new Date()
                      )}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 0.75 }}>
                    {hasEnded && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          p: 0.85,
                          flex: 1,
                          bgcolor: alpha(theme.palette.warning.main, 0.04),
                          borderRadius: "6px",
                        }}
                      >
                        <Timer
                          sx={{
                            fontSize: 14,
                            color: theme.palette.warning.main,
                          }}
                        />
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="#1A1A2E"
                          fontSize="0.7rem"
                        >
                          {getDurationFromDateTime(
                            staff.actual_start,
                            staff.actual_end
                          )}
                        </Typography>
                      </Box>
                    )}
                    {hasEnded && <PunctualityBadge punctuality={punctuality} />}
                  </Box>

                  {staff.location_verified !== undefined && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        p: 0.85,
                        bgcolor: alpha(
                          staff.location_verified
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                          0.04
                        ),
                        borderRadius: "6px",
                      }}
                    >
                      <LocationOn
                        sx={{
                          fontSize: 14,
                          color: staff.location_verified
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="#6B7280"
                        fontWeight={500}
                        sx={{ minWidth: 50, fontSize: "0.68rem" }}
                      >
                        Location
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={
                          staff.location_verified
                            ? theme.palette.success.main
                            : theme.palette.error.main
                        }
                        fontSize="0.7rem"
                      >
                        {staff.location_verified ? "Verified" : "Not Verified"}
                      </Typography>
                      {staff.distance_from_client && (
                        <Chip
                          label={`${staff.distance_from_client}m`}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.55rem",
                            fontWeight: 600,
                            bgcolor: "#F1F5F9",
                            color: "#475467",
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    p: 1.15,
                    bgcolor: "#F8FAFC",
                    borderRadius: "6px",
                    border: "1px dashed #E2E8F0",
                  }}
                >
                  <Pending sx={{ fontSize: 14, color: "#94A3B8" }} />
                  <Typography
                    variant="body2"
                    color="#94A3B8"
                    fontWeight={500}
                    fontSize="0.75rem"
                  >
                    Not Started Yet
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ExecutionDetailsCard;